// ─── Generic preset persistence ─────────────────────────────────────────────
//
// Self-contained generic preference-read/write hook, built directly against
// @repo/services' RTK Query hooks (the same backend `GET/PATCH
// /user/preferences` endpoints `apps/prism2/src/hooks/useUserPreferences.ts`
// already uses for TransactionTable). This intentionally does NOT import that
// app-level hook — `packages/ui` cannot depend on app code (see AGENTS.md
// dependency flow: apps -> @repo/ui -> @repo/services/utils/map) — so this is
// a parallel, minimal implementation of the same pattern, living alongside
// the rest of the DataGrid code per the property-table spec.

import { useCallback, useMemo, useRef } from 'react';
import { useGetAppUserPreferencesQuery, usePatchAppUserPreferenceMutation } from '@repo/services';
import { GridPreset, buildDefaultPreset, buildLastPreset, ColumnConfig, LAST_PRESET_ID } from './types';

const GLOBAL_APP_VALUE = 'Prism';

/**
 * Merges any `defaultColumns` keys missing from a persisted preset's
 * `columns` array into it (appended, ordered after existing columns).
 *
 * Without this, growing `PROPERTY_COLUMN_META`/`COLUMN_META` in code (adding
 * new columns) has no effect on a preset that was already saved to the
 * backend preference blob before those columns existed — `defaultValue` in
 * `usePreferenceValue` below is only used when NOTHING has ever been saved,
 * so a stale persisted preset would otherwise permanently hide newly added
 * columns from the column-visibility list forever. This runs on every read,
 * so newly added columns "self-heal" into every saved preset automatically.
 */
function reconcileColumns<K extends string>(
  presetColumns: ColumnConfig<K>[],
  defaultColumns: ColumnConfig<K>[],
): ColumnConfig<K>[] {
  const existingKeys = new Set(presetColumns.map((c) => c.key));
  const missing = defaultColumns.filter((c) => !existingKeys.has(c.key));
  if (missing.length === 0) return presetColumns;

  const maxOrder = presetColumns.reduce((max, c) => Math.max(max, c.order), -1);
  return [...presetColumns, ...missing.map((c, i) => ({ ...c, order: maxOrder + 1 + i }))];
}

/**
 * Strips `filters`/`sortColumns`/`sortOrders` entries that reference a column
 * key no longer present in `defaultColumns` — e.g. a gated column (like
 * `marketValueUSD`) that disappears from `columnMeta` when the viewing user's
 * permissions change (or is revoked) after the filter/sort was saved.
 *
 * Without this, `filterGridItems`/`sortGridItems` in gridEngine.ts look up
 * `columnMeta[key]` for a stale filter/sort key, get `undefined` back, and
 * `resolveValue()` returns `undefined` for that row's value. For an active
 * *range* filter, `matchesRangeFilter(undefined, range)` computes `Number(undefined)`
 * (`NaN`), which fails the filter for every row — silently emptying the
 * entire grid ("No results found") even though the underlying data loaded
 * successfully. Stripping the stale key here (on every read) self-heals a
 * saved preset the moment the referenced column stops existing.
 */
function reconcileFiltersAndSort<K extends string>(
  preset: GridPreset<K>,
  defaultColumns: ColumnConfig<K>[],
  additionalValidFilterSortKeys: string[],
): GridPreset<K> {
  const validKeys = new Set<string>([...defaultColumns.map((c) => c.key), ...additionalValidFilterSortKeys]);

  const filterKeys = Object.keys(preset.filters);
  const staleFilterKeys = filterKeys.filter((k) => !validKeys.has(k));
  const filters = staleFilterKeys.length === 0
    ? preset.filters
    : Object.fromEntries(filterKeys.filter((k) => validKeys.has(k)).map((k) => [k, preset.filters[k]]));

  const sortIndexesToKeep = preset.sortColumns
    .map((k, i) => (validKeys.has(k) ? i : -1))
    .filter((i) => i !== -1);
  const sortChanged = sortIndexesToKeep.length !== preset.sortColumns.length;
  const sortColumns = sortChanged ? sortIndexesToKeep.map((i) => preset.sortColumns[i]) : preset.sortColumns;
  const sortOrders = sortChanged ? sortIndexesToKeep.map((i) => preset.sortOrders[i]) : preset.sortOrders;

  if (filters === preset.filters && !sortChanged) return preset;
  return { ...preset, filters, sortColumns, sortOrders };
}

/**
 * Runs all per-read preset reconciliation (columns, then filters/sort) — see
 * `reconcileColumns` / `reconcileFiltersAndSort`. Returns the same reference
 * when nothing changed (so `useMemo` identity holds).
 */
function reconcilePreset<K extends string>(
  preset: GridPreset<K>,
  defaultColumns: ColumnConfig<K>[],
  additionalValidFilterSortKeys: string[],
): GridPreset<K> {
  const reconciledColumns = reconcileColumns(preset.columns, defaultColumns);
  const withColumns = reconciledColumns === preset.columns ? preset : { ...preset, columns: reconciledColumns };
  return reconcileFiltersAndSort(withColumns, defaultColumns, additionalValidFilterSortKeys);
}

/**
 * Reads and writes a single keyed value from the shared app user-preferences store (via `useGetAppUserPreferencesQuery`/`usePatchAppUserPreferenceMutation`), falling back to a default until a value has been persisted.
 *
 * @param key - The user preference key to read/write.
 * @param defaultValue - Value returned before the preference has ever been saved.
 * @param appValue - App preference scope; defaults to the global 'Prism' scope.
 * @returns A tuple of [current value, setter, isLoading].
 */
function usePreferenceValue<T>(key: string, defaultValue: T, appValue: string = GLOBAL_APP_VALUE): [T, (value: T | ((prev: T) => T)) => void, boolean] {
  const { data: remote, isLoading } = useGetAppUserPreferencesQuery(appValue);
  const [patchPref] = usePatchAppUserPreferenceMutation();

  const value = (remote?.preferences[key] as T | undefined) ?? defaultValue;

  const valueRef = useRef(value);
  valueRef.current = value;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const setValue = useCallback(
    (action: T | ((prev: T) => T)) => {
      if (isLoadingRef.current) return;
      const prev = valueRef.current;
      const newValue = action instanceof Function ? action(prev) : action;
      patchPref({ appValue, key, value: newValue });
    },
    [appValue, key, patchPref],
  );

  return [value, setValue, isLoading];
}

/**
 * Options for the `useGridPresets` hook, specifying preset keys, default columns, and scope configuration.
 */
export interface UseGridPresetsOptions<K extends string> {
  /** Preference key for the list of saved named presets, e.g. 'propertyTablePresets'. */
  presetsKey: string;
  /** Preference key for which preset id is currently active, e.g. 'propertyTableActivePresetId'. */
  activeIdKey: string;
  /** Default (fresh) column configuration, used to seed the Default/Last presets. */
  defaultColumns: ColumnConfig<K>[];
  /**
   * Extra filter/sort keys that should survive `reconcileFiltersAndSort`'s
   * stale-key cleanup even though they're not part of `defaultColumns` — for
   * a column whose header/cell combine two independently filterable/sortable
   * underlying fields into one visible column (so only ONE of the two field
   * keys, or neither, is a real entry in `defaultColumns`). Without this,
   * every read would silently strip `preset.filters`/`sortColumns` entries
   * for these keys, since `reconcileFiltersAndSort` otherwise treats "not in
   * defaultColumns" as "this column no longer exists" (e.g. a permission-
   * gated column that disappeared) — which is too narrow an assumption once
   * a filter/sort key doesn't correspond 1:1 with a visible column. See
   * TransactionsTableV3.tsx's combined 'assetTypeHealth' column (its
   * `'health'`/`'aimAssetType'` underlying fields) for a real example.
   * Defaults to none.
   */
  additionalValidFilterSortKeys?: string[];
  /** App preference scope. Defaults to the global 'Prism' scope (matches TransactionTable's usage). */
  appValue?: string;
}

/**
 * Return type of `useGridPresets`, providing saved presets, the active preset, dirty state, and all preset management functions.
 */
export interface UseGridPresetsResult<K extends string> {
  presets: GridPreset<K>[];
  activePresetId: string;
  activePreset: GridPreset<K>;
  isLoading: boolean;
  /**
   * True when the current working state (the `__last__` preset) has
   * diverged from the named preset it was forked from (`lastBasePresetId`).
   * Always `false` when `activePresetId` isn't `__last__` — explicitly
   * selecting any preset (via `selectPreset`) immediately clears this, even
   * if `__last__` itself still has old unsaved content sitting in it, since
   * the user has navigated away and that stale state is no longer relevant.
   * Mirrors TransactionTablePresetBar's `isDirty`.
   */
  isDirty: boolean;
  /**
   * Which named preset id the current `__last__` working state was last
   * forked from — defaults to `'default'`. Only meaningful together with
   * `isDirty`; this is set even once the divergence is later resolved (e.g.
   * by editing back to matching values), so always gate on `isDirty`. Equal
   * to `activePresetId` itself whenever `activePresetId !== LAST_PRESET_ID`
   * (see `isDirty`'s doc above for why).
   */
  lastBasePresetId: string;
  /**
   * Patches the current working state and persists it into the `__last__`
   * preset — even when a named preset is currently active. Editing while a
   * named preset is selected forks the change into `__last__` (stamping
   * `basePresetId` to that named preset's id) and switches the active
   * preset to `__last__`, rather than silently overwriting the named preset
   * in place. See `isDirty`/`saveToBasePreset` to surface/commit that state.
   */
  patchActivePreset: (partial: Partial<GridPreset<K>>) => void;
  /** Saves the current working state as a new named preset. */
  saveAsNewPreset: (name: string) => void;
  /**
   * Overwrites `lastBasePresetId`'s saved preset with the current `__last__`
   * working state and switches back to it, clearing the dirty state. No-op
   * if `lastBasePresetId` is `'default'` (read-only — use `saveAsNewPreset`
   * instead) or if there's no `__last__` state to save.
   */
  saveToBasePreset: () => void;
  updatePreset: (presetId: string, partial: Partial<GridPreset<K>>) => void;
  deletePreset: (presetId: string) => void;
  setDefaultPreset: (presetId: string) => void;
  selectPreset: (presetId: string) => void;
}

/**
 * Generic saved-preset persistence, generalized from TransactionTable's
 * `transactionTablePresets`/`transactionTableActivePresetId` preference-key
 * pattern. Any consumer supplies its own distinct key pair so multiple
 * DataGrid tables never collide in the shared per-user preference blob.
 */
export function useGridPresets<K extends string>({
  presetsKey,
  activeIdKey,
  defaultColumns,
  additionalValidFilterSortKeys = [],
  appValue,
}: UseGridPresetsOptions<K>): UseGridPresetsResult<K> {
  const defaultPreset = buildDefaultPreset(defaultColumns);
  const lastPreset = buildLastPreset(defaultPreset);

  const [rawPresets, setPresets, presetsLoading] = usePreferenceValue<GridPreset<K>[]>(
    presetsKey,
    [defaultPreset, lastPreset],
    appValue,
  );
  const [activePresetId, setActivePresetId, activeIdLoading] = usePreferenceValue<string>(
    activeIdKey,
    LAST_PRESET_ID,
    appValue,
  );

  // Reconcile every preset's columns against the current defaultColumns on
  // every read — see reconcilePreset() above. Never mutates the persisted
  // value directly; a write naturally picks up the reconciled shape once the
  // user next edits column visibility (via patchActivePreset/updatePreset).
  const presets = useMemo(
    () => rawPresets.map((p) => reconcilePreset(p, defaultColumns, additionalValidFilterSortKeys)),
    [rawPresets, defaultColumns, additionalValidFilterSortKeys],
  );

  const activePreset =
    presets.find((p) => p.id === activePresetId) ?? reconcilePreset(lastPreset, defaultColumns, additionalValidFilterSortKeys);

  // Ref mirror of activePresetId — patchActivePreset's setPresets callback
  // can run against a stale closure if called in quick succession (e.g. two
  // filter changes in the same tick), so it reads this ref rather than the
  // activePresetId value captured at callback-creation time.
  const activePresetIdRef = useRef(activePresetId);
  activePresetIdRef.current = activePresetId;

  const lastBasePresetId = useMemo(() => {
    // Once the user has explicitly navigated to a different (non-`__last__`)
    // preset, whatever `__last__` still has stashed as its own basePresetId
    // is stale — the previously-active preset is no longer "the thing the
    // current view is unsaved changes on top of". Returning activePresetId
    // itself here (rather than reading __last__'s field) is what makes
    // isDirty below correctly go false the moment you switch away, instead
    // of leaving the old preset stuck showing a dashed "unsaved" border.
    if (activePresetId !== LAST_PRESET_ID) return activePresetId;
    return presets.find((p) => p.id === LAST_PRESET_ID)?.basePresetId ?? 'default';
  }, [activePresetId, presets]);

  const isDirty = useMemo(() => {
    if (activePresetId !== LAST_PRESET_ID) return false;
    const last = presets.find((p) => p.id === LAST_PRESET_ID);
    const base = presets.find((p) => p.id === lastBasePresetId);
    if (!last || !base) return false;
    const sanitize = (p: GridPreset<K>) => JSON.stringify({ ...p, id: '', name: '', basePresetId: '' });
    return sanitize(last) !== sanitize(base);
  }, [activePresetId, presets, lastBasePresetId]);

  const patchActivePreset = useCallback(
    (partial: Partial<GridPreset<K>>) => {
      const currentId = activePresetIdRef.current;
      setPresets((prev) => {
        const current = prev.find((p) => p.id === currentId) ?? prev.find((p) => p.id === 'default') ?? prev[0];
        // Preserve the basePresetId if we're already on __last__, otherwise stamp the current named preset.
        const baseId =
          currentId !== LAST_PRESET_ID ? currentId : (prev.find((p) => p.id === LAST_PRESET_ID)?.basePresetId ?? 'default');
        const updated: GridPreset<K> = { ...current, ...partial, id: LAST_PRESET_ID, name: 'Last', basePresetId: baseId };
        return [...prev.filter((p) => p.id !== LAST_PRESET_ID), updated];
      });
      if (currentId !== LAST_PRESET_ID) setActivePresetId(LAST_PRESET_ID);
    },
    [setPresets, setActivePresetId],
  );

  const saveAsNewPreset = useCallback(
    (name: string) => {
      const newId = `preset_${Date.now()}`;
      setPresets((prev) => [
        ...prev,
        { ...activePreset, id: newId, name, isDefault: false, basePresetId: undefined },
      ]);
      setActivePresetId(newId);
    },
    [activePreset, setPresets, setActivePresetId],
  );

  const saveToBasePreset = useCallback(() => {
    if (lastBasePresetId === 'default') return;
    setPresets((prev) => {
      const last = prev.find((p) => p.id === LAST_PRESET_ID);
      if (!last) return prev;
      return prev.map((p) =>
        p.id === lastBasePresetId ? { ...last, id: p.id, name: p.name, isDefault: p.isDefault, basePresetId: undefined } : p,
      );
    });
    setActivePresetId(lastBasePresetId);
  }, [lastBasePresetId, setPresets, setActivePresetId]);

  const updatePreset = useCallback(
    (presetId: string, partial: Partial<GridPreset<K>>) => {
      setPresets((prev) => prev.map((p) => (p.id === presetId ? { ...p, ...partial } : p)));
    },
    [setPresets],
  );

  const deletePreset = useCallback(
    (presetId: string) => {
      setPresets((prev) => prev.filter((p) => p.id !== presetId));
      if (activePresetId === presetId) setActivePresetId(LAST_PRESET_ID);
    },
    [activePresetId, setPresets, setActivePresetId],
  );

  const setDefaultPreset = useCallback(
    (presetId: string) => {
      setPresets((prev) => prev.map((p) => ({ ...p, isDefault: p.id === presetId })));
    },
    [setPresets],
  );

  const selectPreset = useCallback((presetId: string) => setActivePresetId(presetId), [setActivePresetId]);

  return {
    presets,
    activePresetId,
    activePreset,
    isLoading: presetsLoading || activeIdLoading,
    isDirty,
    lastBasePresetId,
    patchActivePreset,
    saveAsNewPreset,
    saveToBasePreset,
    updatePreset,
    deletePreset,
    setDefaultPreset,
    selectPreset,
  };
}
